/**
 * User Service
 * Handles all user-related API calls
 */

import { fetchAPI, AUTH_TOKEN, REFRESH_TOKEN } from './apiClient';

interface User {
  id: number;
  username: string;
  email: string;
  name?: string;
  is_supporter?: boolean;
  support_next_payment_at?: string | null;
}

interface VerifyOtpResponse {
  token: string;
  refresh_token: string;
  created: boolean;
  username?: string;
  password?: string;
  user: User;
}

export const userService = {
  /**
   * Request a 6-digit sign-in/sign-up code by email. Same response whether
   * the address is new or belongs to an existing account. The email is written
   * in `locale` (backend falls back to English for languages it has no template for).
   */
  async requestOtp(email: string, locale: string): Promise<{ message: string }> {
    return fetchAPI('/api/user/request-otp/', {
      method: 'POST',
      headers: { 'Accept-Language': locale },
      body: JSON.stringify({ email }),
      requiresAuth: false,
    });
  },

  /**
   * Verify the code. Creates the account on first use (created: true) or
   * signs an existing one in (created: false) — same call either way.
   */
  async verifyOtp(email: string, otp: string): Promise<VerifyOtpResponse> {
    return fetchAPI('/api/user/verify-otp/', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
      requiresAuth: false,
    });
  },

  /**
   * Get current user details
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await fetchAPI<{ results?: User[] }>('/api/user/');
      // API returns paginated results, get first user (current user)
      return response.results && response.results.length > 0 ? response.results[0] : null;
    } catch (error) {
      console.error('Error fetching current user:', error);
      return null;
    }
  },

  async getUser(id: number | string): Promise<User> {
    return fetchAPI(`/api/user/${id}/`);
  },

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    return fetchAPI('/api/password-reset/', {
      method: 'POST',
      body: JSON.stringify({ email }),
      requiresAuth: false,
    });
  },

  /**
   * Validate reset token
   */
  async validateResetToken(token: string): Promise<{ status: string }> {
    return fetchAPI('/api/password-reset/validate_token/', {
      method: 'POST',
      body: JSON.stringify({ token }),
      requiresAuth: false,
    });
  },

  /**
   * Reset password with token
   */
  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    return fetchAPI('/api/password-reset/confirm/', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
      requiresAuth: false,
    });
  },

  /**
   * Exchange the refresh token for a fresh access token.
   * Called internally by apiClient on a 401; not normally called directly.
   */
  async refresh(refreshToken: string): Promise<{ token: string }> {
    return fetchAPI('/api/user/refresh/', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
      requiresAuth: false,
    });
  },

  /**
   * Logout user
   */
  logout(): void {
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
  },
};
